import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bot, Plus, Zap, Settings, CheckCircle2, Clock, Play, Pause, Trash2, Loader2, Cpu, BrainCircuit, Code2, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AgenticStudio = ({ project }) => {
  const [agents, setAgents] = useState([]);
  const [mcpServers, setMcpServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [showCreateMCP, setShowCreateMCP] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const [agentsRes, mcpRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/agents`),
        axios.get(`${API}/projects/${project.id}/mcp-servers`)
      ]);
      setAgents(agentsRes.data);
      setMcpServers(mcpRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async (agentData) => {
    try {
      await axios.post(`${API}/projects/${project.id}/agents`, agentData);
      await fetchData();
      setShowCreateAgent(false);
    } catch (error) {
      console.error('Error creating agent:', error);
      alert('Failed to create agent');
    }
  };

  const createMCPServer = async (mcpData) => {
    try {
      await axios.post(`${API}/projects/${project.id}/mcp-servers`, mcpData);
      await fetchData();
      setShowCreateMCP(false);
    } catch (error) {
      console.error('Error creating MCP server:', error);
      alert('Failed to create MCP server');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <BrainCircuit className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">Agentic AI Studio</h2>
              <p className="text-purple-100 mt-1">Create and manage autonomous AI agents for migration tasks</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowCreateAgent(true)}
              className="px-6 py-3 bg-white text-purple-700 rounded-xl hover:bg-purple-50 transition-colors font-medium flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>New Agent</span>
            </button>
            <button
              onClick={() => setShowCreateMCP(true)}
              className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-medium flex items-center space-x-2"
            >
              <Cpu className="w-5 h-5" />
              <span>New MCP Server</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Total Agents</p>
          <p className="text-3xl font-bold text-gray-900">{agents.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Active Agents</p>
          <p className="text-3xl font-bold text-green-600">{agents.filter(a => a.status === 'working').length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">MCP Servers</p>
          <p className="text-3xl font-bold text-gray-900">{mcpServers.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Tasks Completed</p>
          <p className="text-3xl font-bold text-purple-600">{agents.reduce((sum, a) => sum + a.completed_tasks, 0)}</p>
        </div>
      </div>

      {/* Agents Grid */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Agents</h3>
        {agents.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
            <Bot className="w-20 h-20 mx-auto mb-6 text-gray-300" />
            <h4 className="text-xl font-semibold text-gray-900 mb-3">No agents created yet</h4>
            <p className="text-gray-600 mb-6">Create your first AI agent to automate migration tasks</p>
            <button
              onClick={() => setShowCreateAgent(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create First Agent</span>
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} onSelect={setSelectedAgent} />
            ))}
          </div>
        )}
      </div>

      {/* MCP Servers */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">MCP Servers</h3>
        {mcpServers.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100">
            <Cpu className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No MCP servers configured. Create one to enable agent-SAP interactions.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {mcpServers.map((mcp) => (
              <MCPServerCard key={mcp.id} server={mcp} />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateAgent && <CreateAgentModal onClose={() => setShowCreateAgent(false)} onCreate={createAgent} mcpServers={mcpServers} />}
      {showCreateMCP && <CreateMCPModal onClose={() => setShowCreateMCP(false)} onCreate={createMCPServer} />}
      {selectedAgent && <AgentDetailsModal agent={selectedAgent} onClose={() => setSelectedAgent(null)} />}
    </div>
  );
};

const AgentCard = ({ agent, onSelect }) => {
  const statusColors = {
    idle: 'bg-gray-100 text-gray-700',
    assigned: 'bg-yellow-100 text-yellow-700',
    working: 'bg-blue-100 text-blue-700',
    error: 'bg-red-100 text-red-700'
  };

  const typeIcons = {
    code_analyzer: Code2,
    test_generator: CheckCircle2,
    planner: BarChart3,
    executor: Zap
  };

  const Icon = typeIcons[agent.agent_type] || Bot;

  return (
    <div
      onClick={() => onSelect(agent)}
      className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-md">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-900 group-hover:text-purple-600 transition-colors">{agent.name}</h4>
            <p className="text-xs text-gray-600 capitalize">{agent.agent_type.replace('_', ' ')}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[agent.status]}`}>
          {agent.status}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">{agent.description}</p>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center space-x-4 text-sm">
          <div>
            <span className="text-gray-600">Tasks: </span>
            <span className="font-bold text-gray-900">{agent.completed_tasks}</span>
          </div>
          <div>
            <span className="text-gray-600">Success: </span>
            <span className="font-bold text-green-600">{agent.success_rate}%</span>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <p className="text-xs text-gray-600 mb-1">LLM Model</p>
        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
          {agent.llm_config.model}
        </span>
      </div>
    </div>
  );
};

const MCPServerCard = ({ server }) => {
  const statusColors = {
    configured: 'bg-yellow-100 text-yellow-700',
    connected: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700'
  };

  const typeIcons = {
    sap_rfc: '🔌',
    sap_http: '🌐',
    database: '💾',
    file_system: '📁'
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-md text-2xl">
            {typeIcons[server.server_type]}
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-900">{server.name}</h4>
            <p className="text-xs text-gray-600 capitalize">{server.server_type.replace('_', ' ')}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[server.status]}`}>
          {server.status}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">{server.description}</p>

      <div className="space-y-2 pt-4 border-t border-gray-100">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Tools Available:</span>
          <span className="font-bold text-gray-900">{server.tools.length}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Connected Agents:</span>
          <span className="font-bold text-gray-900">{server.connected_agents.length}</span>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-gray-600 mb-2">Available Tools:</p>
        <div className="flex flex-wrap gap-2">
          {server.tools.slice(0, 3).map((tool, idx) => (
            <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
              {tool.name}
            </span>
          ))}
          {server.tools.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
              +{server.tools.length - 3} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateAgentModal = ({ onClose, onCreate, mcpServers }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    agent_type: 'code_analyzer',
    llm_provider: 'openai',
    llm_model: 'gpt-4o',
    temperature: 0.7,
    max_tokens: 2000,
    capabilities: [],
    mcp_servers: [],
    created_by: 'user-001'
  });

  const agentTypes = [
    { id: 'code_analyzer', name: 'Code Analyzer', desc: 'Analyzes ABAP code for compatibility' },
    { id: 'test_generator', name: 'Test Generator', desc: 'Generates comprehensive test cases' },
    { id: 'planner', name: 'Strategic Planner', desc: 'Creates migration strategies and plans' },
    { id: 'executor', name: 'Task Executor', desc: 'Executes remediation and deployment tasks' },
    { id: 'monitoring', name: 'Monitor Agent', desc: 'Monitors system health and progress' }
  ];

  const capabilities = [
    'read_code', 'analyze_structure', 'suggest_fixes', 'generate_tests',
    'create_documentation', 'execute_queries', 'monitor_systems', 'create_reports'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h3 className="text-3xl font-bold text-gray-900 mb-6">Create AI Agent</h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Agent Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ABAP Code Analyzer Alpha"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Agent Type *</label>
              <select
                value={formData.agent_type}
                onChange={(e) => setFormData({ ...formData, agent_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {agentTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Describe what this agent will do"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">LLM Model</label>
              <select
                value={formData.llm_model}
                onChange={(e) => setFormData({ ...formData, llm_model: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="gpt-4o">GPT-4o (Recommended)</option>
                <option value="gpt-4o-mini">GPT-4o-mini (Fast)</option>
                <option value="gpt-5">GPT-5 (Advanced)</option>
                <option value="claude-4-sonnet-20250514">Claude 4 Sonnet</option>
                <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Temperature</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Capabilities</label>
            <div className="grid grid-cols-2 gap-3">
              {capabilities.map((cap) => (
                <label key={cap} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.capabilities.includes(cap)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, capabilities: [...formData.capabilities, cap] });
                      } else {
                        setFormData({ ...formData, capabilities: formData.capabilities.filter(c => c !== cap) });
                      }
                    }}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700 capitalize">{cap.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          {mcpServers.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Connect to MCP Servers</label>
              <div className="space-y-2">
                {mcpServers.map((server) => (
                  <label key={server.id} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.mcp_servers.includes(server.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, mcp_servers: [...formData.mcp_servers, server.id] });
                        } else {
                          setFormData({ ...formData, mcp_servers: formData.mcp_servers.filter(m => m !== server.id) });
                        }
                      }}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">{server.name}</span>
                    <span className="text-xs text-gray-500">({server.server_type})</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium"
            >
              Create Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateMCPModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    server_type: 'sap_rfc',
    endpoint: '',
    port: 3300,
    protocol: 'https',
    auth_type: 'api_key',
    credential_id: '',
    capabilities: []
  });

  const serverTypes = [
    { id: 'sap_rfc', name: 'SAP RFC', desc: 'RFC connection to SAP system' },
    { id: 'sap_http', name: 'SAP HTTP/OData', desc: 'HTTP/OData connection' },
    { id: 'database', name: 'Database', desc: 'Direct database connection' },
    { id: 'file_system', name: 'File System', desc: 'File system access' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h3 className="text-3xl font-bold text-gray-900 mb-6">Create MCP Server</h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Server Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Production ECC RFC Server"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Describe the MCP server purpose"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Server Type *</label>
            <div className="grid grid-cols-2 gap-3">
              {serverTypes.map((type) => (
                <label
                  key={type.id}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.server_type === type.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="server_type"
                    value={type.id}
                    checked={formData.server_type === type.id}
                    onChange={(e) => setFormData({ ...formData, server_type: e.target.value })}
                    className="hidden"
                  />
                  <p className="font-semibold text-gray-900">{type.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{type.desc}</p>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Endpoint *</label>
              <input
                type="text"
                required
                value={formData.endpoint}
                onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                placeholder="sap.example.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Port</label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all font-medium"
            >
              Create MCP Server
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AgentDetailsModal = ({ agent, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignments();
  }, [agent.id]);

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API}/agents/${agent.id}/assignments`);
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-3xl font-bold text-gray-900">{agent.name}</h3>
            <p className="text-gray-600 mt-1">{agent.description}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {/* Agent Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">Status</p>
            <p className="text-lg font-bold text-gray-900 capitalize">{agent.status}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">Completed</p>
            <p className="text-lg font-bold text-green-600">{agent.completed_tasks}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">Success Rate</p>
            <p className="text-lg font-bold text-purple-600">{agent.success_rate}%</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">Model</p>
            <p className="text-sm font-bold text-gray-900">{agent.llm_config.model}</p>
          </div>
        </div>

        {/* Capabilities */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Capabilities</h4>
          <div className="flex flex-wrap gap-2">
            {agent.capabilities.map((cap, idx) => (
              <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium capitalize">
                {cap.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>

        {/* Assignments */}
        <div>
          <h4 className="text-lg font-bold text-gray-900 mb-3">Task Assignments</h4>
          {loading ? (
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
          ) : assignments.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No tasks assigned yet</p>
          ) : (
            <div className="space-y-3">
              {assignments.map((assignment) => (
                <div key={assignment.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{assignment.task_title}</p>
                      <p className="text-sm text-gray-600 capitalize">Type: {assignment.task_type}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      assignment.status === 'completed' ? 'bg-green-100 text-green-700' :
                      assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {assignment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
        >
          Close
        </button>
      </div>
    </div>
  );
};
